import { useEffect, useState } from 'react';
import { newId, ProceduresInput, STANDARD_PROCEDURES, withStandard, type EquipmentDetailDto, type EquipmentFileDto, type Procedure } from '@app/shared';
import { ItemsEditor } from './Steps';
import { api } from '../api';
import { platform } from '../platform';
import { useApp } from '../state';
import { Icon, Sheet } from '../ui';

const isStd = (p: Procedure) => STANDARD_PROCEDURES.some((s) => s.id === p.id);

/**
 * Списки действий единицы: этапы «Выполняемые операции», «Перед началом работы», «В течение смены»,
 * «По окончании смены» и свои. Каждое действие — отдельный пункт: добавить, изменить, переставить, удалить.
 * Изменения сразу действуют для новых проверок; пройденные проверки хранят пункты на момент прохождения.
 */
export function ProceduresEditor({ unit, focus, onClose, onSaved }: { unit: EquipmentDetailDto; focus?: string; onClose: () => void; onSaved: () => void }) {
  const { fail, toast } = useApp();
  const [list, setList] = useState<Procedure[]>(() => withStandard(structuredClone(unit.procedures)));
  const [responsible, setResponsible] = useState(unit.responsible ?? '');
  const [busy, setBusy] = useState(false);
  const [files, setFiles] = useState<EquipmentFileDto[]>(unit.files);

  // Открыли карандашом у этапа — сразу к нему.
  useEffect(() => {
    if (focus && focus !== 'all') document.getElementById(`proc-${focus}`)?.scrollIntoView({ block: 'start' });
  }, [focus]);

  const setProc = (pid: string, fn: (p: Procedure) => Procedure) => setList((l) => l.map((p) => (p.id === pid ? fn(p) : p)));
  const setItems = (pid: string, fn: (items: Procedure['items']) => Procedure['items']) => setProc(pid, (p) => ({ ...p, items: fn(p.items) }));

  async function removeProc(p: Procedure) {
    if (p.items.some((i) => i.text.trim()) && !(await platform.confirm(`Удалить этап «${p.title || 'без названия'}» со всеми пунктами?`))) return;
    setList((l) => l.filter((x) => x.id !== p.id));
  }

  async function save() {
    const parsed = ProceduresInput.safeParse(list.map((p) => ({ ...p, title: p.title.trim() })));
    if (!parsed.success) return fail(new Error(parsed.error.issues[0]?.message ?? 'Проверьте списки'));
    setBusy(true);
    try {
      await api.updateEquipment(unit.id, { procedures: parsed.data, responsible: responsible.trim() || null });
      platform.haptic('success');
      toast('Списки сохранены');
      onSaved();
    } catch (e) {
      fail(e);
    } finally {
      setBusy(false);
    }
  }

  return (
    <Sheet title="Списки действий" onClose={onClose}>
      <p className="small muted" style={{ margin: 0 }}>
        Каждое действие — отдельный пункт. Стрелки ↑↓ меняют порядок, ⓘ — «как это сделать», фото, видео или PDF к пункту. Этапы с отметкой «проверка» оператор проходит с галочками; пустые этапы на странице не видны.
      </p>

      {list.map((p) => (
        <div key={p.id} id={`proc-${p.id}`} className="proc-edit">
          {isStd(p) ? (
            <div className="row">
              <b className="grow">{p.title}</b>
              <span className="small muted">{p.kind === 'check' ? 'проверка' : 'список'}</span>
            </div>
          ) : (
            <>
              <div className="row" style={{ gap: 6 }}>
                <input
                  className="input grow"
                  aria-label="Название этапа"
                  placeholder="Название этапа, например «Еженедельно»"
                  value={p.title}
                  onChange={(e) => setProc(p.id, (x) => ({ ...x, title: e.target.value }))}
                />
                <button type="button" className="icon-btn" aria-label={`Удалить этап ${p.title}`} onClick={() => void removeProc(p)}>
                  <Icon name="trash" size={16} />
                </button>
              </div>
              <label className="check">
                <input type="checkbox" checked={p.kind === 'check'} onChange={(e) => setProc(p.id, (x) => ({ ...x, kind: e.target.checked ? 'check' : 'info' }))} />
                <span>Проверка с галочками</span>
              </label>
            </>
          )}

          <ItemsEditor
            label={p.title || 'этап'}
            items={p.items}
            onChange={(items) => setItems(p.id, () => items)}
            equipmentId={unit.id}
            files={files}
            onFile={(f) => setFiles((l) => [...l, f])}
          />
        </div>
      ))}

      <button type="button" className="btn btn-outline btn-block" onClick={() => setList((l) => [...l, { id: newId('c_'), title: '', kind: 'check', items: [{ id: newId('i'), text: '' }] }])}>
        <Icon name="plus" size={18} />
        Свой этап (например, «Еженедельно»)
      </button>

      <div className="field">
        <label htmlFor="ur">Ответственный за чистоту и порядок</label>
        <input id="ur" className="input" value={responsible} onChange={(e) => setResponsible(e.target.value)} placeholder="Фамилия И.О." />
      </div>

      <button type="button" className="btn btn-primary btn-block" disabled={busy} onClick={() => void save()}>
        Сохранить
      </button>
    </Sheet>
  );
}
